import { Modal, IModal, IFieldValues, Loader, useNotification } from '../../../components';
import { Box, Button, Grid, Stack, Switch, Typography } from '@mui/material';
import { HistoricalContentWrapper, YearlyInputSwitch } from '../GeneralInput.styles';
import {
  FormGrid,
  FormItemLabel,
  TableComponent,
  ControlButton,
  DateRange,
} from '../../../components';
import { useEffect, useMemo, useState } from 'react';
import KpiFormulasModal from '../../../components/Modals/KpiFolmulasModal/KpiFormulasModal';
import {
  HistoricalKpi,
  QuaterlyKpiValues,
  SalonHistoricalAnnualFormMap,
  SalonHistoricalKPI,
  SalonHistoricalKpiMameMap,
  SalonHistoricalQuarterFormMap,
} from '../../../types/SalonHistoricalData';
import { useSalonStore } from '../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { HistoricalKpiColumns } from './types';
import { useTranslation } from 'react-i18next';
import { LocalizedColumns } from '../../../utils';
import { SalonInputTitleDescription } from '../../../types';

export const HistoricalDataModal = ({ open, handleClose }: IModal) => {
  const { t } = useTranslation();
  const {
    isLoading,
    salonHistoricalData,
    salonHistoricalKPI,
    updateSalonHistoricalData,
    fetchSalonHistoricalData,
    fetchSalonHistoricalKPI,
  } = useSalonStore();
  const { salonId } = useParams();
  const [isAnnualMode, setIsAnnualMode] = useState(salonHistoricalData?.annualInputMode || false);
  const currentYear = new Date().getFullYear();
  const [isPreview, setIsPreview] = useState(false);
  const [isFormulasModalOpen, setIsFormulasModalOpen] = useState(false);
  const [currentValues, setCurrentValues] = useState<IFieldValues>();
  const navigate = useNavigate();

  const [year, setYear] = useState<string>((currentYear - 1).toString());
  const { showMessage } = useNotification();

  useEffect(() => {
    open && fetchSalonHistoricalData(salonId, year, showMessage, navigate);
  }, [salonId, year, open]);

  useEffect(() => {
    setIsPreview(false);
    setIsAnnualMode(salonHistoricalData?.annualInputMode || false);
  }, [open]);

  const historicalKpi = ['tnSp', 'tnServiceTransactions', 'tnRetailTransactions'];

  const getInitialValues = useMemo((): Record<string, number> => {
    if (salonHistoricalData === null) return {};
    const initialValues: Record<string, number> = {};

    isAnnualMode
      ? Object.keys(salonHistoricalData?.annualMode)?.forEach(
          (kpi) => (initialValues[kpi] = salonHistoricalData?.annualMode[kpi] ?? null),
        )
      : Object.keys(salonHistoricalData).forEach((kpiTitle) => {
          if (historicalKpi.includes(kpiTitle)) {
            const kpi = salonHistoricalData[kpiTitle as keyof HistoricalKpi];
            Object.keys(kpi).forEach((quarter) => {
              initialValues[`${kpiTitle}${quarter.toUpperCase()}`] =
                kpi[quarter as keyof QuaterlyKpiValues] ?? null;
            });
          } else return;
        });
    return initialValues;
  }, [salonHistoricalData, isAnnualMode]);

  const getRelevantYears = (): string[] => {
    return [currentYear - 2, currentYear - 1, currentYear].map((item) => item.toString());
  };

  const handleModalClose = () => {
    handleClose();
  };

  const rowsPrepare = () => {
    const rows = [];
    for (const key in SalonHistoricalKpiMameMap) {
      const kpi = salonHistoricalKPI?.[key as keyof SalonHistoricalKPI];
      rows.push({
        kpi: SalonHistoricalKpiMameMap[key as keyof typeof SalonHistoricalKpiMameMap],
        avedaSalonData: `${kpi?.prefix || ''}${kpi?.value || '-'}${kpi?.suffix || ''}`,
      });
    }

    return rows;
  };

  const content = () => {
    return (
      <HistoricalContentWrapper>
        {!isLoading && !isPreview && (
          <>
            <YearlyInputSwitch
              control={
                <Switch
                  onChange={() => setIsAnnualMode(!isAnnualMode)}
                  color="primary"
                  checked={isAnnualMode}
                />
              }
              label={t('yearlyInput')}
              labelPlacement="start"
              data-testid="yearly-view-switch"
            />
            <Typography variant="h6">
              <FormItemLabel
                label={t('dateRange')}
                description={
                  'Please specify the year and quarter for which you are conducting the Quarterly Business Review (QBR).'
                }
              />
            </Typography>
            <Grid container mt={1} spacing={2}>
              <DateRange
                cellSize={12}
                title={t('year')}
                items={getRelevantYears()}
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </Grid>
            {Object.entries(
              isAnnualMode ? SalonHistoricalAnnualFormMap : SalonHistoricalQuarterFormMap,
            ).map((item) => (
              <FormGrid
                key={item[0]}
                formName={item[0]}
                formDescription={
                  SalonInputTitleDescription[item[0] as keyof typeof SalonInputTitleDescription]
                }
                fields={item[1]}
                initialValues={getInitialValues}
                onChangeFieldValues={(values) =>
                  setCurrentValues((prevState) => ({ ...prevState, ...values }))
                }
              />
            ))}
          </>
        )}
        {!isLoading && isPreview && (
          <>
            <Stack justifyContent="space-between" mb={2}>
              <Box>
                <Typography variant="h6">{t('results')}</Typography>
                <Typography variant="body2">{`${t('cy')} ${year}`}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Button variant="outlined" onClick={() => setIsFormulasModalOpen(true)}>
                  {t('kpiFormulas')}
                </Button>
              </Box>
            </Stack>
            <TableComponent
              rows={rowsPrepare()}
              columns={LocalizedColumns(HistoricalKpiColumns, t, true)}
              dataTestId="historical-results-table"
            />
            <KpiFormulasModal
              open={isFormulasModalOpen}
              handleClose={() => setIsFormulasModalOpen(false)}
              isHistorical
            />
          </>
        )}
        <Loader isModal isLoading={isLoading} />
      </HistoricalContentWrapper>
    );
  };

  const calculate = () => {
    const payload = {
      annualMode: {
        tnSpBeginning: currentValues?.tnSpBeginning || null,
        tnSpEnd: currentValues?.tnSpEnd || null,
        tnServiceTransactions: currentValues?.tnServiceTransactions || null,
        tnRetailTransactions: currentValues?.tnRetailTransactions || null,
      },
      tnSp: {
        q1: currentValues?.tnSpQ1 || null,
        q4: currentValues?.tnSpQ4 || null,
      },
      tnServiceTransactions: {
        q1: currentValues?.tnServiceTransactionsQ1 || null,
        q2: currentValues?.tnServiceTransactionsQ2 || null,
        q3: currentValues?.tnServiceTransactionsQ3 || null,
        q4: currentValues?.tnServiceTransactionsQ4 || null,
      },
      tnRetailTransactions: {
        q1: currentValues?.tnRetailTransactionsQ1 || null,
        q2: currentValues?.tnRetailTransactionsQ2 || null,
        q3: currentValues?.tnRetailTransactionsQ3 || null,
        q4: currentValues?.tnRetailTransactionsQ4 || null,
      },
    };

    updateSalonHistoricalData(salonId, year, isAnnualMode, payload, showMessage, navigate, () =>
      fetchSalonHistoricalKPI(salonId, year, showMessage, navigate),
    );
    setIsPreview(true);
  };

  const saveAndClose = () => {
    handleClose();
  };

  const handleBack = () => {
    setIsPreview(false);
  };

  const footer = () => (
    <>
      <ControlButton
        label={isPreview ? t('back') : t('cancel')}
        disabled={isLoading}
        action={isPreview ? handleBack : handleClose}
      />
      <ControlButton
        label={isPreview ? t('close') : t('calculate')}
        disabled={isLoading}
        action={isPreview ? saveAndClose : calculate}
        variant={'contained'}
      />
    </>
  );

  return (
    <Modal
      open={open}
      handleClose={handleModalClose}
      header={t('historicalDataInput')}
      mainContent={content()}
      buttons={footer()}
      dataTestId="historical-data-modal"
      maxWidth="lg"
      fullWidth
      contentDividers
    />
  );
};

export default HistoricalDataModal;
